import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { usePixelChat } from "@/hooks/usePixelChat";
import { useProjects } from "@/hooks/useProjects";
import { useSupabaseMCP } from "@/hooks/useSupabaseMCP";
import { useVersionHistory } from "@/hooks/useVersionHistory";
import { useExtractedProjectFiles } from "@/hooks/useExtractedProjectFiles";
import { useProjectFilesRef } from "@/hooks/useProjectFilesRef";
import { useTerminalBuffer } from "@/hooks/useTerminalBuffer";
import { ProjectView } from "@/components/v0/ProjectView";
import { AppHeader } from "@/components/v0/V0Header";
import { Sidebar } from "@/components/v0/Sidebar";
import { HomeContent } from "@/components/v0/HomeContent";
import { ProjectsGrid } from "@/components/v0/ProjectsGrid";
import { ProjectSettingsDialog } from "@/components/v0/ProjectSettingsDialog";
import { toast } from "sonner";
import { LimitReachedModal } from "@/components/LimitReachedModal";

type HomeView = "create" | "projects";

const Index = () => {
  const { projectId: urlProjectId } = useParams<{ projectId?: string }>();
  const navigate = useNavigate();

  const [showHome, setShowHome] = useState(!urlProjectId);
  const [homeView, setHomeView] = useState<HomeView>("create");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [versionFiles, setVersionFiles] = useState<{ name: string; content: string }[]>([]);
  const [versionFilesLoading, setVersionFilesLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeChatId, setActiveChatId] = useState<string | null>(urlProjectId || null);

  // Credit limit modal state
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitInfo, setLimitInfo] = useState<{
    plan_type: string;
    messages_remaining_today: number;
    messages_remaining_month: number;
  } | null>(null);

  // Get Supabase connection status (for AI context)
  // Fix: Scope to activeChatId or urlProjectId to avoid Global connection leak
  const { isConnected: supabaseIsConnected, selectedProjectName: supabaseProjectName, selectedProject: supabaseProjectRef, anonKey: supabaseAnonKey } = useSupabaseMCP(activeChatId || urlProjectId);

  // Project files ref for AI context (avoids circular dependency)
  const { updateFiles: updateProjectFilesRef, getFiles: getProjectFiles } = useProjectFilesRef();

  // Terminal output buffer for AI diagnostics
  const { appendOutput: appendTerminalOutput, getOutput: getTerminalOutput } = useTerminalBuffer();

  const {
    messages,
    input,
    handleInputChange,
    handleSubmit,
    isLoading,
    stop,
    append,
    setInput,
    setMessages,
    loadChat,
    isReady, // Get isReady from hook
  } = usePixelChat({
    chatId: activeChatId,
    projectId: activeChatId,
    supabaseConnected: supabaseIsConnected,
    supabaseProjectName: supabaseProjectName,
    supabaseProjectRef: supabaseProjectRef,
    supabaseAnonKey: supabaseAnonKey,
    getProjectFiles, // Pass getter for dynamic file context
    getTerminalOutput, // Pass getter for terminal output diagnostics
    onLimitReached: (info) => {
      setLimitInfo(info);
      setShowLimitModal(true);
    },
  });

  const {
    projects,
    activeProjectId,
    activeProject,
    isLoading: projectsLoading,
    error: projectsError,
    setActiveProjectId,
    createProject,
    updateProject,
    deleteProject,
    getProject,
    loadProjects,
  } = useProjects();

  const {
    versions,
    currentVersion,
    currentVersionIndex,
    canGoBack,
    canGoForward,
    isAtLatest,
    isLoading: versionsLoading,
    loadVersions,
    loadVersionFiles,
    createVersion,
    rollbackVersion,
    goToPrevious,
    goToNext,
    goToVersion,
    goToLatest,
    resetHistory,
    addLocalVersion,
  } = useVersionHistory();

  const lastMessageIdRef = useRef<string | null>(null);

  // Pending message to send after chatId updates
  const pendingMessageRef = useRef<string | null>(null);

  // Track previous loading state for auto-save (ref used in effect after sandpackFiles)
  const wasLoadingRef = useRef(false);

  useEffect(() => {
    if (projectsError) {
      toast.error("Erreur projets", { description: projectsError });
    }
  }, [projectsError, toast]);

  // Track if we've loaded this URL project already
  const loadedUrlProjectRef = useRef<string | null>(null);

  // Effect to send pending message after chatId updates
  useEffect(() => {
    if (pendingMessageRef.current && activeChatId && isReady) {
      const messageToSend = pendingMessageRef.current;
      pendingMessageRef.current = null; // Clear immediately to prevent double-send

      // Small delay to ensure React has fully updated
      setTimeout(async () => {
        console.log('[Index] Sending pending message for chatId:', activeChatId);
        if (typeof append === 'function') {
          await append({ role: 'user', content: messageToSend });
        } else {
          toast.error("Erreur", { description: "Le système n'est pas encore prêt, veuillez réessayer." });
        }
      }, 50);
    }
  }, [activeChatId, isReady, append, toast]);

  // Load project from URL on mount
  useEffect(() => {
    // Only attempt to load if:
    // 1. Chat system is ready (token loaded)
    // 2. We have a URL project ID
    // 3. We haven't already loaded this project
    // 4. There's no pending message (new project being created)
    if (isReady && urlProjectId && loadedUrlProjectRef.current !== urlProjectId && !pendingMessageRef.current) {
      console.log('[Index] Loading project from URL:', urlProjectId);
      loadedUrlProjectRef.current = urlProjectId; // Mark as loading/loaded
      setActiveChatId(urlProjectId);
      setActiveProjectId(urlProjectId);
      setShowHome(false);
      loadChat(urlProjectId);
      loadVersions(urlProjectId);
    }
  }, [urlProjectId, isReady, setActiveProjectId, loadChat, loadVersions]);

  // Load files when viewing an older version
  useEffect(() => {
    const loadFiles = async () => {
      if (currentVersion && activeChatId && !isAtLatest) {
        setVersionFilesLoading(true);
        setVersionFiles([]);
        const loadedFiles = await loadVersionFiles(activeChatId, currentVersion.id);
        setVersionFiles(loadedFiles);
        setVersionFilesLoading(false);
      } else if (isAtLatest) {
        setVersionFiles([]);
        setVersionFilesLoading(false);
      }
    };
    loadFiles();
  }, [currentVersion?.id, isAtLatest, activeChatId, loadVersionFiles]);

  const handleNewProject = useCallback(async () => {
    const project = await createProject(`Projet ${projects.length + 1}`);
    if (project) {
      setMessages([]);
      resetHistory();
      setActiveChatId(null);
      setShowHome(false);
      toast.success("Projet créé", { description: `${project.name} a été créé avec succès.` });
    }
  }, [projects.length, createProject, setMessages, resetHistory, toast]);

  const handleSelectProject = useCallback(async (id: string) => {
    setActiveProjectId(id);
    resetHistory();
    setShowHome(false);
    setActiveChatId(id);

    // Update URL to persist project
    navigate(`/project/${id}`, { replace: true });

    // Load existing messages and versions for this project
    await loadChat(id);
    loadVersions(id);
  }, [setActiveProjectId, resetHistory, loadChat, navigate, loadVersions]);

  const handleDeleteProject = useCallback(async (id: string) => {
    const success = await deleteProject(id);
    if (success) {
      toast.success("Projet supprimé", { description: "Le projet a été supprimé avec succès." });
    }
  }, [deleteProject, toast]);

  const handleReset = useCallback(() => {
    setMessages([]);
    resetHistory();
    setActiveProjectId(null);
    setActiveChatId(null);
    setShowHome(true);

    // Clear URL
    navigate('/', { replace: true });
  }, [setMessages, resetHistory, setActiveProjectId, navigate]);

  // Handle project rename
  const handleRenameProject = useCallback(async (newName: string): Promise<{ success: boolean; error?: string }> => {
    if (!activeProjectId) return { success: false, error: 'No active project' };

    const result = await updateProject(activeProjectId, { name: newName });

    if (result) {
      return { success: true };
    }
    return { success: false, error: 'Un projet avec ce nom existe déjà' };
  }, [activeProjectId, updateProject]);

  // Handle version rollback
  const handleRollback = useCallback(async (versionId: string) => {
    if (!activeProjectId) return;

    const result = await rollbackVersion(activeProjectId, versionId);
    if (result) {
      toast.success("Version restaurée", { description: `Restauré à partir de V${result.version.versionNumber}` });
    }
  }, [activeProjectId, rollbackVersion, toast]);

  const handleHomeMessage = useCallback(async (message: string) => {
    // Créer un nouveau chat avec un ID unique
    const newChatId = crypto.randomUUID();

    // Pass the full message as firstMessage for AI naming. 
    // projectName (arg 1) is used as fallback or initial title if AI fails/delays, but backend will override it with AI name.
    const project = await createProject(message.slice(0, 50).trim(), undefined, message);

    setMessages([]);
    resetHistory();
    setShowHome(false);

    // Utiliser l'ID du projet créé comme chatId
    const chatId = project?.id || newChatId;

    // Store the message to send - the useEffect will trigger when chatId updates
    pendingMessageRef.current = message;

    // Update chatId - this will trigger the useEffect that sends the pending message
    setActiveChatId(chatId);

    // Update URL to persist project
    navigate(`/project/${chatId}`, { replace: true });
  }, [setMessages, resetHistory, createProject, navigate]);

  const handleBackToHome = useCallback(() => {
    setShowHome(true);
    setActiveProjectId(null);
    setActiveChatId(null);
    setMessages([]);
    resetHistory();
    loadedUrlProjectRef.current = null; // Clear URL project tracking
    navigate('/', { replace: true });
  }, [setActiveProjectId, setMessages, resetHistory, navigate]);

  // Extract project files from AI messages with debouncing
  // SAFETY: AI SDK messages can have undefined content for tool-only messages
  const extractedFiles = useExtractedProjectFiles(
    messages.map((m: any) => ({ role: m.role, content: m.content ?? '' })),
    isLoading,
    500 // 500ms debounce during streaming
  );

  // Sync extracted files to ref for AI context (avoids circular dependency)
  useEffect(() => {
    if (Object.keys(extractedFiles.files).length > 0) {
      updateProjectFilesRef(extractedFiles.files);
      console.log('[Index] Updated project files for AI context:', Object.keys(extractedFiles.files).length, 'files');
    }
  }, [extractedFiles.files, updateProjectFilesRef]);

  // Auto-save version when AI finishes generating
  useEffect(() => {
    // Detect transition from loading to not loading
    if (wasLoadingRef.current && !isLoading && activeChatId) {
      // AI just finished - save version with current files
      const files = extractedFiles.files;
      const fileArray = Object.entries(files).map(([name, content]) => ({
        name,
        content: typeof content === 'string' ? content : String(content),
      }));

      if (fileArray.length > 0) {
        console.log('[Index] AI finished, saving version with', fileArray.length, 'files');
        createVersion(activeChatId, fileArray).then((version) => {
          if (version) {
            console.log('[Index] Version', version.versionNumber, 'created');
          }
        });
      }
    }
    wasLoadingRef.current = isLoading;
  }, [isLoading, activeChatId, extractedFiles.files, createVersion]);

  // Get files to display
  const displayFiles = !isAtLatest && versionFiles.length > 0 ? versionFiles : [];
  const previewIsLoading = isLoading || versionFilesLoading;

  // Si on est dans un projet, on affiche la vue projet
  if (!showHome) {
    return (
      <>
        <ProjectView
          messages={messages}
          input={input}
          setInput={setInput}
          handleInputChange={handleInputChange}
          handleSubmit={handleSubmit}
          isLoading={isLoading}
          stop={stop}
          projectId={activeProjectId || urlProjectId || undefined}
          projectName={activeProject?.name || "Projet"}
          onRenameProject={handleRenameProject}
          sandpackFiles={extractedFiles.files}
          sandpackDependencies={extractedFiles.dependencies}
          sandpackShellActions={extractedFiles.shellActions}
          sandpackDeletedFiles={extractedFiles.deletedFiles}
          previewIsLoading={previewIsLoading}
          onNavigateToHome={handleBackToHome}
          append={append}
          versions={versions}
          currentVersionIndex={currentVersionIndex}
          onRollback={handleRollback}
          isVersionsLoading={versionsLoading}
        />
        <ProjectSettingsDialog
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          projectId={activeProjectId || ''}
          projectName={activeProject?.name || 'Projet'}
          chatId={activeChatId}
          versionId={currentVersion?.id}
          files={displayFiles}
        />
        <LimitReachedModal
          isOpen={showLimitModal}
          onClose={() => setShowLimitModal(false)}
          planType={limitInfo?.plan_type || 'free'}
          messagesRemainingToday={limitInfo?.messages_remaining_today || 0}
          messagesRemainingMonth={limitInfo?.messages_remaining_month || 0}
        />
      </>
    );
  }

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background">
      <Sidebar
        projects={projects.map(p => ({
          id: p.id,
          name: p.name,
          createdAt: p.createdAt,
          updatedAt: p.updatedAt,
        }))}
        activeProjectId={activeProjectId}
        onSelectProject={handleSelectProject}
        onNewProject={handleNewProject}
        onDeleteProject={handleDeleteProject}
        isLoading={projectsLoading}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        currentView={homeView}
        onViewChange={setHomeView}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <AppHeader />

        <div className="flex-1 overflow-hidden">
          {homeView === "create" ? (
            <HomeContent
              onSendMessage={handleHomeMessage}
              isLoading={isLoading}
            />
          ) : (
            <ProjectsGrid
              projects={projects.map(p => ({
                id: p.id,
                name: p.name,
                createdAt: p.createdAt,
                updatedAt: p.updatedAt,
              }))}
              activeProjectId={activeProjectId}
              onSelectProject={handleSelectProject}
              onNewProject={handleNewProject}
              onDeleteProject={handleDeleteProject}
              isLoading={projectsLoading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Index;
